'use client';

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useQuality } from '@/components/shared/QualityProvider';
import { StaggerText } from '@/components/ui/stagger-text';

// Dedicated mobile rendering of the four post-portfolio chapters. The
// desktop ChapterStation depends on the TrailingBlob's parked screen
// coordinates, clip-path mask wipes, blur cascades, and an absolute-
// positioned 42vw column — none of which translate to a phone. So at
// low tier this component renders instead (ChapterStation self-gates to
// null), with a vertical card stack, native scroll, and one quiet
// fade+rise reveal per card.
//
// Headlines are the only content shown today; lead/tail copy lives in
// the translation files for future use but the current desktop design
// also doesn't surface it. Keeping mobile + desktop in lockstep on what's
// visible avoids the "did you miss anything?" feeling between tiers.

type ChapterCardProps = {
  chapter: string;
  headline: string;
  accent?: boolean;
  cta?: { label: string; href: string };
};

function ChapterCard({ chapter, headline, accent = false, cta }: ChapterCardProps) {
  const ref = useRef<HTMLElement>(null);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (typeof IntersectionObserver === 'undefined') {
      // Fallback for very old browsers: skip the reveal animation entirely.
      // Synchronous setState in an effect is fine here — it runs once at
      // mount and the alternative (kicking it out of the effect) would
      // require a SSR-incompatible branch in the render path.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setShown(true);
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShown(true);
          observer.disconnect();
        }
      },
      { threshold: 0.2, rootMargin: '0px 0px -10% 0px' },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={ref}
      className={`relative flex min-h-[78svh] flex-col justify-center px-6 py-20 transition-[opacity,transform] duration-[900ms] ease-out ${
        shown ? 'translate-y-0 opacity-100' : 'translate-y-6 opacity-0'
      }`}
      data-chapter={chapter}
    >
      {/* StaggerText is a motion.div — role+aria-level preserve the
          heading semantics for assistive tech while letting per-letter
          motion run inside. `overflow-hidden` clips each letter to its
          line so the y:100% start state is hidden behind the baseline
          (chars rise into view instead of appearing from below it). */}
      <StaggerText
        role="heading"
        aria-level={2}
        text={headline}
        direction="bottom"
        stagger={0.04}
        className={`m-0 overflow-hidden font-mono text-[clamp(2rem,8.8vw,2.8rem)] font-extrabold uppercase leading-[1.04] tracking-tight ${
          accent ? 'text-(--color-accent)' : 'text-(--color-muted)'
        }`}
        style={
          accent
            ? {
                textShadow:
                  '0 0 18px color-mix(in srgb, var(--color-accent) 45%, transparent), 0 0 4px color-mix(in srgb, var(--color-accent) 25%, transparent)',
              }
            : undefined
        }
      />

      {cta && (
        <a
          href={cta.href}
          className="group/cta mt-8 inline-flex w-fit items-center gap-4 py-1"
        >
          <span
            aria-hidden
            className="block h-11 w-1 bg-(--color-accent)"
          />
          <span className="flex items-center gap-3 font-mono text-base uppercase tracking-[5px] text-(--color-muted) transition-colors duration-300 ease-out group-hover/cta:text-(--color-accent)">
            {cta.label}
            <span
              aria-hidden
              className="text-2xl leading-none text-(--color-accent) transition-transform duration-500 ease-out group-hover/cta:translate-x-2"
            >
              →
            </span>
          </span>
        </a>
      )}
    </section>
  );
}

export function MobileInfoSection({
  chapters,
  mobileOnly = true,
}: {
  chapters: ChapterCardProps[];
  // Home gates this to low tier (the desktop ChapterStation renders instead).
  // The /grid stack variant renders it on every tier, so it opts out.
  mobileOnly?: boolean;
}): ReactNode {
  const { tier } = useQuality();
  if (mobileOnly && tier !== 'low') return null;

  return (
    <div className="mx-auto w-full max-w-3xl">
      {chapters.map((c) => (
        <ChapterCard key={c.chapter} {...c} />
      ))}
    </div>
  );
}
